import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Trash2, AlertTriangle, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react'
import { formatDisplayDateTime } from '@/lib/dateUtils'
import { Button } from './ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Checkbox } from './ui/checkbox'
import { ClientDropdown } from './ui/client-dropdown'
import { reconciliationApi, ProcessedFileData } from '../lib/api'
import { useToast } from '../hooks/use-toast'

interface DeleteDialogState {
  open: boolean
  file: ProcessedFileData | null
  hasCommittedLinks: boolean
  acknowledgeRisk: boolean
  isDeleting: boolean
}

export const ProcessedFilesPage: React.FC = () => {
  const { toast } = useToast()
  const [files, setFiles] = useState<ProcessedFileData[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedClient, setSelectedClient] = useState<string>('')
  const [skip, setSkip] = useState(0)
  const limit = 25

  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    file: null,
    hasCommittedLinks: false,
    acknowledgeRisk: false,
    isDeleting: false,
  })

  const fetchFiles = useCallback(async () => {
    setLoading(true)
    try {
      const response = await reconciliationApi.getProcessedFiles(
        selectedClient || undefined,
        skip,
        limit
      )
      setFiles(response.files)
      setTotal(response.total)
    } catch (error) {
      console.error('Failed to fetch processed files:', error)
      toast({
        title: 'Error',
        description: 'Failed to load processed files',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [selectedClient, skip, toast])

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  const handleClientChange = (clientId: string) => {
    setSelectedClient(clientId)
    setSkip(0) // Reset pagination when client changes
  }

  const openDeleteDialog = (file: ProcessedFileData) => {
    setDeleteDialog({
      open: true,
      file,
      hasCommittedLinks: file.has_committed_links,
      acknowledgeRisk: false,
      isDeleting: false,
    })
  }

  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      file: null,
      hasCommittedLinks: false,
      acknowledgeRisk: false,
      isDeleting: false,
    })
  }

  const handleDelete = async () => {
    if (!deleteDialog.file) return

    // If has committed links, require acknowledgment
    if (deleteDialog.hasCommittedLinks && !deleteDialog.acknowledgeRisk) {
      toast({
        title: 'Acknowledgment required',
        description: 'Please acknowledge the risks before deleting.',
        variant: 'destructive',
      })
      return
    }

    setDeleteDialog(prev => ({ ...prev, isDeleting: true }))

    try {
      const response = await reconciliationApi.deleteTransactionFile(
        deleteDialog.file.id,
        deleteDialog.hasCommittedLinks // force if has committed links
      )

      toast({
        title: 'File deleted',
        description: `Deleted file and ${response.transactions_deleted} transaction(s).`,
      })

      closeDeleteDialog()
      fetchFiles() // Refresh the list
    } catch (error) {
      console.error('Failed to delete file:', error)
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Failed to delete file',
        variant: 'destructive',
      })
      setDeleteDialog(prev => ({ ...prev, isDeleting: false }))
    }
  }

  const formatDate = (dateString: string) => {
    return formatDisplayDateTime(dateString) || dateString
  }

  const formatFileSize = (bytes: number | null) => {
    if (bytes === null) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="h-3 w-3" />
            Completed
          </span>
        )
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="h-3 w-3" />
            Processing
          </span>
        )
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3" />
            Failed
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        )
    }
  }

  const totalPages = Math.ceil(total / limit)
  const currentPage = Math.floor(skip / limit) + 1

  return (
    <div className="page-shell space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/reconciliation"
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Reconciliation
          </Link>
          <h1 className="text-2xl font-bold text-foreground page-main-heading">
            Processed Files
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            View and manage uploaded bank statement files
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="w-64">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Client
          </label>
          <ClientDropdown
            value={selectedClient}
            onValueChange={handleClientChange}
            placeholder="All Clients"
            multiple={false}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : files.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-500">
            <p>No processed files found</p>
            {selectedClient && (
              <p className="text-sm mt-1">Try selecting a different client or clearing the filter</p>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Filename</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Upload Date</TableHead>
                <TableHead className="text-right">Transactions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900 truncate max-w-xs">
                        {file.original_filename}
                      </span>
                      <span className="text-xs text-gray-500">
                        {file.mime_type} &middot; {formatFileSize(file.size_bytes)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-700">{file.client_name || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-700">{formatDate(file.ingested_at)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end">
                      <span className="font-medium">{file.transaction_count}</span>
                      {file.has_committed_links && (
                        <span className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {file.committed_link_count} committed
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(file.status)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(file)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {!loading && files.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} files
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {deleteDialog.hasCommittedLinks && (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              )}
              Delete File
            </DialogTitle>
            <DialogDescription>
              {deleteDialog.file && (
                <>
                  Are you sure you want to delete{' '}
                  <strong>{deleteDialog.file.original_filename}</strong>?
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {deleteDialog.file && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-500">Transactions:</div>
                  <div className="font-medium">{deleteDialog.file.transaction_count}</div>
                  <div className="text-gray-500">Client:</div>
                  <div className="font-medium">{deleteDialog.file.client_name || '-'}</div>
                </div>
              </div>

              {deleteDialog.hasCommittedLinks && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-800">Warning: Committed Mirage Links</p>
                      <p className="text-sm text-amber-700 mt-1">
                        This file has {deleteDialog.file.committed_link_count} transaction(s) committed to Mirage.
                        Deleting this file will NOT reverse those accounting entries. This may cause data
                        inconsistency in your accounting system.
                      </p>

                      <div className="mt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={deleteDialog.acknowledgeRisk}
                            onCheckedChange={(checked) =>
                              setDeleteDialog(prev => ({
                                ...prev,
                                acknowledgeRisk: checked === true,
                              }))
                            }
                          />
                          <span className="text-sm text-amber-800">
                            I understand the risks and want to proceed
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-600">
                This will permanently delete the file and all {deleteDialog.file.transaction_count} associated transaction(s).
                This action cannot be undone.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteDialog.isDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={
                deleteDialog.isDeleting ||
                (deleteDialog.hasCommittedLinks && !deleteDialog.acknowledgeRisk)
              }
            >
              {deleteDialog.isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete File'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
